/**
 * connectResume — WHICH conversation a finished sign-in belongs to
 * (owner report 2026-09-03: "after signing in it came back to the chat but
 * nothing occurred … it should proceed right where it was left off").
 *
 * The rule lives here, apart from the 8,000-line component that applies it,
 * because it is the part that can be wrong in a way nobody notices: resuming
 * the WRONG thread posts an answer into a conversation that never asked, and
 * resuming a stale one answers a question the user abandoned an hour ago.
 * Both read as Prest0n talking to itself. A rule this consequential should be
 * executable by a test rather than inferred from a component.
 *
 * Three inputs, one decision:
 *   - what the sign-in surface ANNOUNCED (an explicit origin, when the caller
 *     knew its own thread) — always wins;
 *   - what the chat REMEMBERED (the thread that was open when a chat-lane
 *     sign-in started) — the fallback, because the click handler that opens
 *     the surface lives outside the chat and cannot know the thread;
 *   - how long ago that was.
 *
 * Nothing is resumed on a mismatch, and nothing is resumed on a guess.
 */

/** The thread a finished sign-in should carry on in. */
export interface ConnectResumeOrigin {
  threadId: string;
  resumeText?: string;
}

/** What the chat remembered when a chat-lane sign-in surface opened. */
export interface RememberedConnectOrigin {
  appName: string;
  threadId: string;
  /** ms epoch. */
  at: number;
}

export interface ResolveConnectResumeInput {
  /** The app the completed sign-in was for. */
  appName: string;
  /** An origin the surface announced, when the caller supplied one. */
  announced?: ConnectResumeOrigin | null;
  /** The chat's own memory of where this started. */
  remembered?: RememberedConnectOrigin | null;
  now: number;
  windowMs: number;
}

/**
 * The thread to resume, or null for "say nothing".
 *
 * `null` is the safe answer and the common one: a sign-in from the
 * Integrations page has no conversation behind it at all.
 */
export function resolveConnectResumeOrigin(
  input: ResolveConnectResumeInput,
): ConnectResumeOrigin | null {
  const appName = String(input.appName || '').trim();
  if (!appName) return null;

  // An explicit origin is the caller telling us, not us inferring. It is not
  // subject to the app-name match or the window: a caller that knows its own
  // thread knows better than this rule does.
  const announcedThread = String(input.announced?.threadId || '').trim();
  if (announcedThread) {
    const resumeText = String(input.announced?.resumeText || '').trim();
    return { threadId: announcedThread, ...(resumeText ? { resumeText } : {}) };
  }

  const remembered = input.remembered;
  if (!remembered) return null;
  // The remembered pairing is only evidence for the app it was recorded for.
  // Two sign-ins in one session are ordinary; answering the first thread for
  // the second app is not.
  if (String(remembered.appName || '').trim() !== appName) return null;
  const threadId = String(remembered.threadId || '').trim();
  if (!threadId) return null;
  const age = input.now - Number(remembered.at || 0);
  // A negative age is a clock that moved, not a fresh sign-in.
  if (!Number.isFinite(age) || age < 0 || age >= input.windowMs) return null;
  return { threadId };
}
